import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, X, Check, Clock, AlertCircle, Info, Calendar, MessageSquare, FileText } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { notificationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useChatWebSocketContext } from '../context/ChatWebSocketContext';
import { useTier } from '../context/TierContext';
import { formatRelativeNotificationTime } from '../lib/time';

function getNotificationLink(referenceType, referenceId) {
  if (!referenceType || !referenceId) return null;
  if (referenceType === 'quote') return { pathname: '/quotes', search: `?quote_id=${referenceId}` };
  if (referenceType === 'event') return { pathname: '/event-connect', search: `?event_id=${referenceId}` };
  if (referenceType === 'complaint') return { pathname: '/complaints', search: `?complaint_id=${referenceId}` };
  if (referenceType === 'announcement') return { pathname: '/dashboard' };
  if (referenceType === 'chat' || referenceType === 'message') return { pathname: '/chat', state: { openConversationId: referenceId } };
  return null;
}

const NotificationBell = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [timeTick, setTimeTick] = useState(Date.now());
  const dropdownRef = useRef(null);
  const { member } = useAuth();
  const wsContext = useChatWebSocketContext();
  const { isRouteAccessible } = useTier();

  useEffect(() => {
    if (member?.member_id) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000); // Fallback poll every 60s (real-time via WebSocket when connected)
      return () => clearInterval(interval);
    }
  }, [member?.member_id]);

  useEffect(() => {
    const registerNotificationHandler = wsContext?.registerNotificationHandler;
    if (!registerNotificationHandler || !member?.member_id) return;
    const unregister = registerNotificationHandler((notification) => {
      setUnreadCount((c) => c + 1);
      setNotifications((prev) => [{ ...notification, _receivedAt: Date.now() }, ...prev].slice(0, 10));
    });
    return unregister;
  }, [wsContext?.registerNotificationHandler, member?.member_id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setTimeTick(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsApi.getUnreadCount(member.member_id);
      setUnreadCount(response.data.unread_count ?? 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsApi.getNotifications(member.member_id, { limit: 10 });
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsApi.markAsRead(notificationId, member.member_id);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    const link = getNotificationLink(notification.reference_type, notification.reference_id);
    const isNavigable = Boolean(link) && isRouteAccessible(link.pathname);
    if (!isNavigable) {
      return;
    }
    if (!notification.is_read) {
      handleMarkAsRead(notification.notification_id);
    }
    setIsOpen(false);
    navigate(link);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead(member.member_id);
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const quoteTypes = [
      'quote', 'quote_created', 'quote_updated', 'quote_assigned', 'bid_received', 'bid_updated',
      'bid_nominated', 'quote_rfq_completed', 'quote_rfq_accepted', 'quote_reminder',
      'quote_payment_details_requested', 'quote_payment_received', 'quote_completed',
    ];
    if (quoteTypes.includes(type)) return <FileText size={16} className="text-blue-500" />;
    if (type === 'message') return <MessageSquare size={16} className="text-green-500" />;
    if (type === 'event' || type === 'event_registration' || type === 'event_payment') return <Calendar size={16} className="text-purple-500" />;
    if (type === 'alert') return <AlertCircle size={16} className="text-red-500" />;
    if (type === 'reminder') return <Clock size={16} className="text-amber-500" />;
    if (type === 'complaint' || type === 'complaint_status' || type === 'clarification_reply') return <AlertCircle size={16} className="text-orange-500" />;
    if (type === 'announcement') return <Info size={16} className="text-indigo-500" />;
    return <Info size={16} className="text-gray-500" />;
  };

  const formatTime = (notification) => {
    const receivedAt = notification._receivedAt;
    if (receivedAt != null && (Date.now() - receivedAt) < 2 * 60 * 1000) {
      return 'Just now';
    }
    return formatRelativeNotificationTime(notification.created_at, new Date(timeTick));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        type="button"
        onClick={handleToggle}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        aria-label={unreadCount > 0 ? `Open notifications (${unreadCount} unread)` : 'Open notifications'}
        data-testid="notification-bell"
      >
        <Bell size={22} className="text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="fixed left-1/2 top-[4.5rem] z-50 mt-0 flex max-h-[calc(100dvh-5rem)] w-[calc(100vw-1rem)] max-w-80 -translate-x-1/2 flex-col rounded-lg border bg-white shadow-lg sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 sm:max-w-none sm:translate-x-0">
          {/* Header */}
          <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b px-3 py-3 sm:px-4">
            <h3 className="min-w-0 font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const link = getNotificationLink(notification.reference_type, notification.reference_id);
                const isNavigable = Boolean(link) && isRouteAccessible(link.pathname);

                return (
                  <div
                    key={notification.notification_id}
                    className={`px-4 py-3 border-b last:border-b-0 ${
                      isNavigable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                    } ${!notification.is_read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.is_read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        {notification.action_pending && (
                          <p className="text-xs text-blue-600 mt-1 font-medium">
                            Action: {notification.action_pending}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {formatTime(notification)}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex-shrink-0 border-t px-3 py-2 text-center sm:px-4">
            <Link
              to="/notifications"
              className="text-sm text-blue-600 hover:text-blue-800"
              onClick={() => setIsOpen(false)}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
